/**
 * ClassDetailsModal — Modal de detalhes para turmas (group scheduling)
 *
 * Exibe lista de participantes de uma turma com:
 * - Nome e telefone de cada participante
 * - Status (confirmado, pendente, cancelado, atendido)
 * - Ações por linha (Confirmar, Cancelar, Marcar Presença)
 *
 * NOTA: Suporta até 1000 participantes (CHECK do banco). Virtualização não
 * foi implementada no MVP porque limites realistas do plano são baixos
 * (FREE=10, BASIC=30, PRO ainda assim atende sem virtualização). Marcado como
 * bônus futuro B3 caso turmas grandes (>100) se tornem comuns — instalar
 * `react-window` e reintroduzir a FixedSizeList.
 */

import { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Check as CheckIcon,
  Cancel as CancelIcon,
  DoneAll as DoneAllIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { getAppointmentStatusStyles, getAppointmentStatusLabel } from '../constants/statusConfig';
import PhoneDisplay from './ui/PhoneDisplay';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getStatus(appointment) {
  if (!appointment) return 'pending';
  if (appointment.cancelledAt) return 'cancelled';
  if (appointment.attendedAt) return 'completed';
  const aptTime = new Date(appointment.startTime);
  if (new Date() > aptTime && appointment.confirmedAt) return 'no_show';
  if (appointment.confirmedAt) return 'confirmed';
  return 'pending';
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ParticipantRow({ appointment, canWrite, onConfirm, onCancel, onMarkAttended, style }) {
  const status = getStatus(appointment);
  const statusStyles = getAppointmentStatusStyles(status);

  const isConfirmed = appointment.confirmedAt && !appointment.cancelledAt;
  const isCancelled = appointment.cancelledAt;
  const isAttended = appointment.attendedAt;

  return (
    <ListItem
      style={style}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider',
        p: 1,
      }}
    >
      {/* Nome, Telefone, Status */}
      <Box flex={1}>
        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
          <Typography variant="body2" fontWeight="bold" noWrap>
            {appointment.clientName || 'Sem nome'}
          </Typography>
          {statusStyles && (
            <Chip
              label={getAppointmentStatusLabel(status)}
              size="small"
              color={statusStyles.chipColor}
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Box>

        {appointment.clientPhone && (
          <Box display="flex" alignItems="center" gap={0.5}>
            <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <PhoneDisplay phone={appointment.clientPhone} variant="caption" />
          </Box>
        )}
      </Box>

      {/* Ações */}
      {canWrite && (
        <Box display="flex" gap={0.5}>
          {!isConfirmed && !isCancelled && (
            <Tooltip title="Confirmar">
              <IconButton
                size="small"
                onClick={() => onConfirm?.(appointment)}
                sx={{ color: 'success.main' }}
              >
                <CheckIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {!isCancelled && (
            <Tooltip title="Cancelar">
              <IconButton
                size="small"
                onClick={() => onCancel?.(appointment)}
                sx={{ color: 'error.main' }}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {isConfirmed && !isAttended && !isCancelled && (
            <Tooltip title="Marcar Presença">
              <IconButton
                size="small"
                onClick={() => onMarkAttended?.(appointment)}
                sx={{ color: 'info.main' }}
              >
                <DoneAllIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
    </ListItem>
  );
}

// ─── Main Modal ───────────────────────────────────────────────────────────────

export default function ClassDetailsModal({
  group,
  open,
  onClose,
  canWrite = true,
  onConfirm,
  onCancel,
  onMarkAttended,
}) {
  const items = useMemo(() => group?.items || [], [group?.items]);
  const schedule = group?.schedule;

  const counts = useMemo(() => {
    const confirmed = items.filter(a => a.confirmedAt && !a.cancelledAt).length;
    const cancelled = items.filter(a => a.cancelledAt).length;
    const attended = items.filter(a => a.attendedAt).length;
    const pending = items.length - confirmed - cancelled;

    return { confirmed, cancelled, attended, pending };
  }, [items]);

  const handleConfirm = (appointment) => {
    if (onConfirm) {
      onConfirm(appointment);
    }
  };

  const handleCancel = (appointment) => {
    if (onCancel) {
      onCancel(appointment);
    }
  };

  const handleMarkAttended = (appointment) => {
    if (onMarkAttended) {
      onMarkAttended(appointment);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {/* Header */}
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6" component="span" fontWeight="bold">
            {schedule?.name || 'Detalhes da Turma'}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            {items.length} participante{items.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      {/* Stats Row */}
      <Box sx={{ p: 2, bgcolor: 'background.default', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Chip
          label={`${counts.confirmed} confirmados`}
          size="small"
          color="success"
          variant="outlined"
        />
        <Chip
          label={`${counts.pending} pendentes`}
          size="small"
          color="warning"
          variant="outlined"
        />
        {counts.attended > 0 && (
          <Chip
            label={`${counts.attended} atendidos`}
            size="small"
            color="info"
            variant="outlined"
          />
        )}
        {counts.cancelled > 0 && (
          <Chip
            label={`${counts.cancelled} cancelados`}
            size="small"
            color="error"
            variant="outlined"
          />
        )}
      </Box>

      <Divider />

      {/* Content */}
      <DialogContent sx={{ p: 0, flex: 1 }}>
        {items.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Typography color="text.secondary">Nenhum participante registrado.</Typography>
          </Box>
        ) : (
          // Lista comum. Para turmas com >100 participantes considerar bônus B3
          // (virtualização via react-window) — limites de plano hoje não exigem.
          <Box sx={{ maxHeight: 480, overflowY: 'auto' }}>
            <List sx={{ p: 0 }}>
              {items.map((appointment, idx) => (
                <ParticipantRow
                  key={appointment.id ?? idx}
                  appointment={appointment}
                  canWrite={canWrite}
                  onConfirm={handleConfirm}
                  onCancel={handleCancel}
                  onMarkAttended={handleMarkAttended}
                />
              ))}
            </List>
          </Box>
        )}
      </DialogContent>

      {/* Actions */}
      <Divider />
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
